import React, { useEffect, useState } from "react";
import { Table, Form } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { Helmet } from "react-helmet";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { filterByParams } from "../../utils/api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CsvDownloader from "react-csv-downloader";
import { CiExport } from "react-icons/ci";
import withAuth from "../../utils/withAuth";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { numberFormat } from "../../components/numberFormat";


const RADIAN = Math.PI / 180;

// Draws line + % label outside the donut
const renderDonutLabelWithLine = (props) => {
    const {
        cx,
        cy,
        midAngle,
        outerRadius,
        value,        // our % value (share_percent)
        fill,
        payload,
    } = props;

    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const labelText = `${payload.name} (${Number(value).toFixed(1)}%)`;

    // start: at outer edge of slice
    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;

    // middle point: a bit further out from slice
    const mx = cx + (outerRadius + 10) * cos;
    const my = cy + (outerRadius + 10) * sin;

    // end point: horizontal offset (left or right)
    const ex = mx + (cos >= 0 ? 22 : -22);
    const ey = my;

    const textAnchor = cos >= 0 ? "start" : "end";


    return (
        <g>
            {/* connector line */}
            <polyline
                points={`${sx},${sy} ${mx},${my} ${ex},${ey}`}
                stroke={fill || "#999"}
                strokeWidth={1}
                fill="none"
            />

            {/* optional small circle at end of line */}
            <circle cx={ex} cy={ey} r={2} fill={fill || "#999"} />

            {/* percentage text */}
            <text
                x={ex + (cos >= 0 ? 4 : -4)}
                y={ey}
                textAnchor={textAnchor}
                dominantBaseline="central"
                style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    fill: "#333",
                    letterSpacing: "0.5px",
                }}
            >
                {labelText}
            </text>

        </g>
    );
};

// Colors for donut slices
const DONUT_COLORS = [
    "#4285F4",
    "#FBBC05",
    "#34A853",
    "#EA4335",
    "#A142F4",
    "#00ACC1",
    "#f41ed7ff",
    "#7CB342",
    "#35fdb0ff",
    "#8D6E63",
];

const TopProductsByClusterReport = () => {
    // NOTE: these are actually CLUSTER-HEAD rows now
    const [clusterSummary, setClusterSummary] = useState([]);          // left table
    const [topProductsForCluster, setTopProductsForCluster] = useState([]); // right table
    const [selectedClusterId, setSelectedClusterId] = useState("All"); // store cluster_head_id
    const [topLimit, setTopLimit] = useState(5); // Top 5 default
    const [isLoading, setIsLoading] = useState(false);

    const [dateRange, setDateRange] = useState({
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
    });

    const adminid = localStorage.getItem("admindata");
    const navigate = useNavigate();

    // ---------- COMMON FETCH FUNCTION ----------
    const loadReport = async (options = {}) => {
        setIsLoading(true);

        // clusterHead = id or "All"
        const clusterHead =
            options.clusterHead !== undefined
                ? options.clusterHead
                : selectedClusterId || "All";

        const payload = {
            clusterHead, // backend expects this key
            limit: options.limit ?? topLimit ?? 5,
            startDate: options.startDate ?? dateRange.startDate,
            endDate: options.endDate ?? dateRange.endDate,
        };

        try {
            const res = await filterByParams(
                "super-admin-get-top-product-by-cluster-head-report-one-months",
                payload
            );

            if (res.data.status === 1) {
                const data = res.data;

                // left table
                setClusterSummary(data.cluster || []);
                // right table
                setTopProductsForCluster(data.products || []);

                // current selected cluster id coming from API (if provided)
                const currentClusterId =
                    data.clusterHeadId ??               // camelCase
                    data.cluster_head_id ??             // snake_case
                    clusterHead;                        // fallback (we sent in payload)

                setSelectedClusterId(currentClusterId);

                setDateRange({
                    startDate: data.start_date || payload.startDate,
                    endDate: data.end_date || payload.endDate,
                });
            }
        } catch (err) {
            console.error("Error fetching top products by cluster:", err);
        }

        setIsLoading(false);
    };

    // ---------- INITIAL LOAD ----------
    useEffect(() => {
        if (!adminid) {
            navigate("/");
        } else {
            loadReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminid]);

    // ---------- HANDLERS ----------
    const handleClusterChange = async (e) => {
        const newClusterId = e.target.value; // "All" or cluster_head_id
        setSelectedClusterId(newClusterId);
        await loadReport({ clusterHead: newClusterId });
    };

    const handleLimitChange = async (e) => {
        const newLimit = Number(e.target.value);
        setTopLimit(newLimit);
        await loadReport({ limit: newLimit });
    };

    const handleDateRangeApply = async (event, picker) => {
        const selectedRange = {
            startDate: picker.startDate.format("YYYY-MM-DD"),
            endDate: picker.endDate.format("YYYY-MM-DD"),
        };
        setDateRange(selectedRange);
        await loadReport(selectedRange);
    };

    // ---------- EXPORT CONFIG ----------
    const clusterColumns = [
        { id: "cluster_head_name", displayName: "Cluster Head" },
        { id: "total_orders", displayName: "Total Orders" },
        { id: "total_revenue", displayName: "Revenue" },
        { id: "top_product_name", displayName: "Top Product" },
        { id: "share_percent", displayName: "% Share (Qty)" },
    ];

    const clusterExportData = clusterSummary.map((row) => ({
        cluster_head_name: row.cluster_head_name || "",
        total_orders: row.total_orders || 0,
        total_revenue: row.total_revenue || 0,
        top_product_name: row.top_product_name || "",
        share_percent: row.share_percent || 0,
    }));

    const productColumns = [
        { id: "p_name", displayName: "Product" },
        { id: "p_sku_number", displayName: "SKU" },
        { id: "total_orders", displayName: "Orders" },
        { id: "total_qty", displayName: "Qty" },
        { id: "total_revenue", displayName: "Revenue" },
        { id: "share_percent", displayName: "% Share (Qty)" },
        { id: "avg_ticket", displayName: "Avg Ticket" },
    ];

    const productExportData = topProductsForCluster.map((p) => ({
        p_name: p.p_name || "",
        p_sku_number: p.p_sku_number || "",
        total_orders: p.total_orders || 0,
        total_qty: p.total_qty || 0,
        total_revenue: p.total_revenue || 0,
        share_percent: p.share_percent || 0,
        avg_ticket: p.avg_ticket || 0,
    }));

    // Heading: show cluster_head_name, not id
    const headingClusterName =
        selectedClusterId === "All" || selectedClusterId === "All Cluster Head"
            ? "All Cluster Head"
            : (
                clusterSummary.find(
                    (c) => String(c.cluster_head_id) === String(selectedClusterId)
                ) || {}
            ).cluster_head_name || "All Cluster Head";

    const donutData = (clusterSummary || [])
        .map((row) => {
            // share_percent might be "19.49", "19.49%", or number
            const raw = row.share_percent ?? 0;
            const share = parseFloat(raw); // safely convert to number

            return {
                name: row.cluster_head_name || "NA",
                value: Number.isNaN(share) ? 0 : share,
            };
        })
        .filter((item) => item.value > 0)
        .slice(0, 10); // max 10 slices so it stays readable

    return (
        <div className="top-products-state-report misDashboard">
            <Helmet>
                <title>SATKARTAR: Top Product By Cluster Head</title>
            </Helmet>

            <ContentWrapper>
                <ReportNavBar />

                {/* Top bar: Date range picker */}
                <div className="d-flex justify-content-end mb-3">
                    <DateRangePicker
                        initialSettings={{
                            startDate: moment(dateRange.startDate, "YYYY-MM-DD"),
                            endDate: moment(dateRange.endDate, "YYYY-MM-DD"),
                        }}
                        onApply={handleDateRangeApply}
                    >
                        <button className="btn btn-sm btndaterange">
                            {moment(dateRange.startDate, "YYYY-MM-DD").format(
                                "DD MMM YYYY"
                            )}{" "}
                            –{" "}
                            {moment(dateRange.endDate, "YYYY-MM-DD").format(
                                "DD MMM YYYY"
                            )}
                        </button>
                    </DateRangePicker>
                </div>

                <div className="row" style={{ marginBottom: "50px" }}>
                    {/* LEFT: CLUSTER HEAD RANKING */}
                    <div className="col-lg-5 mb-3">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                                <h6
                                    className="mb-0 text-uppercase"
                                    style={{ letterSpacing: "0.05em" }}
                                >
                                    Cluster Head Ranking
                                </h6>

                                <div className="d-flex align-items-center gap-2">
                                    <CsvDownloader
                                        filename={`CLUSTER_HEAD_RANKINGS_${moment().format(
                                            "YYYY_MM_DD"
                                        )}`}
                                        extension=".csv"
                                        columns={clusterColumns}
                                        datas={clusterExportData}
                                    >
                                        <button
                                            className="btn btn-xs d-flex align-items-center gap-1"
                                            style={{ background: "#0dcaf0", color: "#fff" }}
                                        >
                                            <CiExport />
                                            <span>Export</span>
                                        </button>
                                    </CsvDownloader>

                                    <Form.Select
                                        size="sm"
                                        style={{ width: 180 }}
                                        onChange={handleClusterChange}
                                        value={selectedClusterId}
                                    >
                                        <option value="All">All Cluster Head</option>
                                        {clusterSummary.map((row) => (
                                            <option
                                                key={row.cluster_head_id}
                                                value={row.cluster_head_id}
                                            >
                                                {row.cluster_head_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>

                            <div className="card-body p-0">
                                <Table
                                    className="table-sm table-fixeds"
                                    responsive
                                    striped
                                    bordered
                                    hover
                                >
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 40 }}>#</th>
                                            <th>Cluster Head</th>
                                            <th className="text-end">Total Orders</th>
                                            <th className="text-end">Revenue</th>
                                            <th>Top Product</th>
                                            <th className="text-end">% Share (Qty)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            [...Array(5)].map((_, rowIdx) => (
                                                <tr key={rowIdx}>
                                                    {Array.from({ length: 6 }).map((__, colIdx) => (
                                                        <td key={colIdx}>
                                                            <Skeleton height={16} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : clusterSummary.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="text-center">
                                                    No data available
                                                </td>
                                            </tr>
                                        ) : (
                                            clusterSummary.map((row, idx) => (
                                                <tr
                                                    key={row.cluster_head_id || idx}
                                                    // click row = load report for that cluster head
                                                    onClick={() =>
                                                        loadReport({ clusterHead: row.cluster_head_id })
                                                    }
                                                    style={{
                                                        cursor: "pointer",
                                                        backgroundColor:
                                                            String(selectedClusterId) ===
                                                                String(row.cluster_head_id)
                                                                ? "#f5f7ff"
                                                                : "transparent",
                                                    }}
                                                >
                                                    <td>{idx + 1}</td>
                                                    <td>{row.cluster_head_name || "UNKNOWN"}</td>
                                                    <td>
                                                        {(row.total_orders || 0).toLocaleString("en-IN")}
                                                    </td>
                                                    <td>
                                                        {numberFormat((row.total_revenue || 0).toLocaleString("en-IN"))}
                                                    </td>
                                                    {/* Top Product column */}
                                                    <td>{row.top_product_name || "-"}</td>
                                                    <td>
                                                        {row.share_percent || 0}%
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                                <div className="d-flex justify-content-center align-items-center py-3">
                                    {donutData.length > 0 && (
                                        <PieChart width={600} height={300}>
                                            <Pie
                                                data={donutData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={70}
                                                outerRadius={95}
                                                paddingAngle={3}
                                                label={renderDonutLabelWithLine}
                                                labelLine={false}
                                            >
                                                {donutData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={DONUT_COLORS[index % DONUT_COLORS.length]}
                                                    />
                                                ))}
                                            </Pie>

                                            {/* center title like "Disease" in your sample */}
                                            <text
                                                x="50%"
                                                y="50%"
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fontSize={12}
                                                fill="#333"
                                            >
                                                Cluster Share
                                            </text>

                                            <Tooltip formatter={(v, _n, payload) => [`${v}%`, payload.payload.name]} />
                                        </PieChart>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: TOP PRODUCTS FOR SELECTED CLUSTER HEAD */}
                    <div className="col-lg-7 mb-3">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                                <h6
                                    className="mb-0 text-uppercase"
                                    style={{ letterSpacing: "0.05em" }}
                                >
                                    Top Products – {headingClusterName}
                                </h6>

                                <div className="d-flex align-items-center gap-2">
                                    <CsvDownloader
                                        filename={`TOP_PRODUCTS_${headingClusterName.replace(
                                            /\s+/g,
                                            "_"
                                        )}_${moment().format("YYYY_MM_DD")}`}
                                        extension=".csv"
                                        columns={productColumns}
                                        datas={productExportData}
                                    >
                                        <button
                                            className="btn btn-xs d-flex align-items-center gap-1"
                                            style={{ background: "#0dcaf0", color: "#fff" }}
                                        >
                                            <CiExport />
                                            <span>Export</span>
                                        </button>
                                    </CsvDownloader>

                                    <Form.Select
                                        size="sm"
                                        style={{ width: 100 }}
                                        value={topLimit}
                                        onChange={handleLimitChange}
                                    >
                                        <option value={5}>Top 5</option>
                                        <option value={10}>Top 10</option>
                                        <option value={20}>Top 20</option>
                                    </Form.Select>
                                </div>
                            </div>

                            <div className="card-body p-0">
                                <Table
                                    className="table-sm table-fixeds"
                                    responsive
                                    striped
                                    bordered
                                    hover
                                >
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 40 }}>#</th>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th>Orders</th>
                                            <th>Qty</th>
                                            <th>Revenue</th>
                                            <th>% Share (Qty)</th>
                                            <th>Avg Ticket</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            [...Array(5)].map((_, rowIdx) => (
                                                <tr key={rowIdx}>
                                                    {Array.from({ length: 8 }).map(
                                                        (_, colIdx) => (
                                                            <td key={colIdx}>
                                                                <Skeleton height={16} />
                                                            </td>
                                                        )
                                                    )}
                                                </tr>
                                            ))
                                        ) : topProductsForCluster.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="text-center">
                                                    No data available
                                                </td>
                                            </tr>
                                        ) : (
                                            topProductsForCluster.map((p, index) => (
                                                <tr key={p.p_sku_number || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{p.p_name}</td>
                                                    <td>{p.p_sku_number}</td>
                                                    <td>
                                                        {(p.total_orders || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {(p.total_qty || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                    <td>
                                                        ₹{" "}
                                                        {(p.total_revenue || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                    <td>{p.share_percent || 0}%</td>
                                                    <td>
                                                        ₹{" "}
                                                        {(p.avg_ticket || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(TopProductsByClusterReport);
